import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@territory/ui';
import { WEDGE_NOTICE as N } from '@territory/tokens';
import { useAuth } from '@territory/client/AuthContext';
import {
  VIEW_AS_ENDED_EVENT,
  exitViewAs,
  viewAsHasEnded,
  viewAsInstallState,
} from '@territory/client/viewAs';
import { BRAND, brandScreen, BrandWordmark } from '../wedgeBrand';

export const VIEW_AS_HOST_ID = 'rv-viewas-host';

/* The words on screen, kept in one place so the copy test and the component agree. */
export const viewAsCopy = {
  banner: (name: string) => `View only as ${name}.`,
  returnLabel: 'Return to my account',
  endedTitle: 'This view-only session has ended',
  endedBody: 'Nothing was changed. You can close this tab and go back to your own account.',
  failedTitle: 'View only could not start',
  failedBody:
    'This browser would not give the view its own private storage, so it was not opened. Nothing was shown and your own account was not touched. Close this tab and try another browser.',
};

function useEnded(): boolean {
  const [ended, setEnded] = useState(viewAsHasEnded());
  useEffect(() => {
    const on = () => setEnded(true);
    window.addEventListener(VIEW_AS_ENDED_EVENT, on);
    return () => window.removeEventListener(VIEW_AS_ENDED_EVENT, on);
  }, []);
  return ended;
}

/** Make room for the stripe IN the page flow: the body becomes a column, the stripe's host sits above #root and
 *  #root takes the rest. A transformed #root also stays the containing block for the app's own fixed layers, so
 *  they fill the space under the stripe instead of sliding under it. When the view has ended, #root is hidden and
 *  the host takes the whole page for the ended notice. */
function useHost(active: boolean, ended: boolean): HTMLElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!active) return undefined;
    const root = document.getElementById('root');
    const el = document.createElement('div');
    el.id = VIEW_AS_HOST_ID;
    document.body.insertBefore(el, root ?? document.body.firstChild);
    const bodyStyle = document.body.getAttribute('style');
    const rootStyle = root?.getAttribute('style') ?? null;
    document.body.style.display = 'flex';
    document.body.style.flexDirection = 'column';
    document.body.style.height = '100dvh';
    document.body.style.overflow = 'hidden';
    if (root) {
      root.style.flex = '1 1 auto';
      root.style.minHeight = '0';
      root.style.position = 'relative';
      root.style.transform = 'translateZ(0)';
      root.style.overflow = 'hidden';
    }
    setHost(el);
    return () => {
      if (bodyStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', bodyStyle);
      if (root) {
        if (rootStyle === null) root.removeAttribute('style');
        else root.setAttribute('style', rootStyle);
      }
      el.remove();
      setHost(null);
    };
  }, [active]);
  useEffect(() => {
    if (!host) return;
    const root = document.getElementById('root');
    host.style.flex = ended ? '1 1 auto' : '0 0 auto';
    if (root) root.style.display = ended ? 'none' : '';
  }, [host, ended]);
  return host;
}

export function ViewAsFailedScreen() {
  return <Screen title={viewAsCopy.failedTitle} body={viewAsCopy.failedBody} />;
}

function Screen({ title, body }: { title: string; body: string }) {
  return (
    <div role="alert" style={{ ...brandScreen, gap: 14 }}>
      <BrandWordmark size={18} />
      <h1 style={{ fontFamily: BRAND.fontBody, fontSize: 20, margin: 0 }}>{title}</h1>
      <p style={{ maxWidth: 420, margin: 0, lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

/**
 * The persistent view-as stripe (issue 2902). Renders nothing in an ordinary tab. In a view-as tab it shows
 * "View only as <name>." with a Return button above the app, and once the session is over (Return pressed, or
 * the token expired) a full-page "ended" notice replaces the app. When the tab could not be isolated, main.tsx
 * mounts ViewAsFailedScreen in place of the app, never the owner's own account.
 */
export function ViewAsBanner() {
  const { user } = useAuth();
  const state = viewAsInstallState();
  const ended = useEnded();
  const host = useHost(state === 'active', ended);

  if (state !== 'active' || !host) return null;
  if (ended) return createPortal(<Screen title={viewAsCopy.endedTitle} body={viewAsCopy.endedBody} />, host);

  const name = user?.name?.trim() || user?.email || 'this user';
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      data-testid="view-as-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '6px 16px',
        background: N.offlineBg,
        color: N.offlineText,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: 0.3,
        boxShadow: N.offlineShadow,
      }}
    >
      <span>{viewAsCopy.banner(name)}</span>
      <Button appearance="wedge-notice" noticeLook="in-app-copy" onClick={() => void exitViewAs()}>
        {viewAsCopy.returnLabel}
      </Button>
    </div>,
    host,
  );
}
